/**
 * Analytics Events
 *
 * This file contains all analytics event enums used throughout the application.
 * Events are organized by feature/module to match the event tracking documentation.
 *
 * See: /EVENT_TRACKING_DOCUMENTATION.md for complete details
 */

export const AnalyticsEvent = {
	/**
	 * Admin Dashboard Events
	 * Tracks admin dashboard interactions
	 */
	AdminDashboard: {
		LinkVisit: 'Admin Dashboard Link Click',
		PageView: 'Admin Dashboard Page View',
		OneOnOnesActionLinkClick: 'Admin Dashboard One On Ones Action Link Click', // link: Engagement, Adoption, Learn More
		ParticipationActionLinkClick: 'Admin Dashboard Participation Action Link Click', // link: Participation, Points Activity, Learn More
		ProgramsActionLinkClick: 'Admin Dashboard Programs Action Link Click', // link: Manage Awards, Awards Setup, Manage Celebrations, Setup Celebrations, Manage Incentives, Setup Incentives, Learn More
		CostsActionLinkClick: 'Admin Dashboard Costs Action Link Click', // link: Plans & Billing, Billing History, Reward Balance, Redemption Activity, Maximize Your ROI
	},

	/**
	 * Bizy Events
	 * Tracks Bizy chat interactions across all surfaces
	 */
	Bizy: {
		ChatOpened: 'Bizy Chat Opened',
		ChatClosed: 'Bizy Chat Closed',
		ChatSeen: 'Bizy Chat Seen',
		PanelAutoOpened: 'Bizy Panel Auto Opened',
		ChatMessageSent: 'Bizy Chat Message Sent',
		ResponseReceived: 'Bizy Response Received',
		GenerationCancelled: 'Bizy Generation Cancelled',
		ConversationReset: 'Bizy Conversation Reset',
		FeedbackPositive: 'Bizy Feedback Positive',
		FeedbackNegativeStarted: 'Bizy Feedback Negative Started',
		FeedbackNegativeSubmitted: 'Bizy Feedback Negative Submitted',
		FeedbackNegativeCancelled: 'Bizy Feedback Negative Cancelled',
		FeedbackCleared: 'Bizy Feedback Cleared',
		ConversationDeleted: 'Bizy Conversation Deleted',
		ConversationRenamed: 'Bizy Conversation Renamed',
		ConversationTitleGenerated: 'Bizy Conversation Title Generated',
		GreetingShown: 'Bizy Greeting Shown',
		// New-segment greeter callout for unread People Browser moments (BIZY-638).
		GreeterShown: 'Bizy Greeter Shown',
		GreeterCtaClicked: 'Bizy Greeter Cta Clicked',
		// No-moments greeter empty state + connector nudge (BIZY-700).
		GreeterEmptyShown: 'Bizy Greeter Empty Shown',
		GreeterAddConnectorClicked: 'Bizy Greeter Add Connector Clicked',
		ChatResponseReceived: 'Bizy Chat Response Received',
		ChatError: 'Bizy Chat Error',
		VoiceInputUsed: 'Bizy Voice Input Used',
		VoiceInputError: 'Bizy Voice Input Error',
		SuggestedPageClicked: 'Bizy Suggested Page Clicked',
		SuggestionClicked: 'Bizy Suggestion Clicked',
		PromptLibraryMenuOpened: 'Bizy Prompt Library Menu Opened',
		PromptLibraryOpened: 'Bizy Prompt Library Opened',
		PromptLibraryClosed: 'Bizy Prompt Library Closed',
		PromptLibraryPromptSelected: 'Bizy Prompt Library Prompt Selected',
		PromptLibraryFavoriteToggled: 'Bizy Prompt Library Favorite Toggled',
		PromptLibraryConnectorClicked: 'Bizy Prompt Library Connector Clicked',
		PromptLibrarySurpriseMe: 'Bizy Prompt Library Surprise Me',
		MeetBizyPromoShown: 'Meet Bizy Promo Shown',
		MeetBizyPromoDismissed: 'Meet Bizy Promo Dismissed',
		MeetBizyPromoTryItOutClicked: 'Meet Bizy Promo Try It Out Clicked',
		MeetBizyPromoLearnMoreClicked: 'Meet Bizy Promo Learn More Clicked',
		MeetBizyPromoBackClicked: 'Meet Bizy Promo Back Clicked',
		// Contextual `/` invocation from supported text inputs (give box, comment reply)
		ContextualInvoked: 'Bizy Contextual Invoked',
		ContextualInserted: 'Bizy Contextual Inserted',
		ContextualDismissed: 'Bizy Contextual Dismissed',
		// Per-row Create CTA on a MeetingPeopleCard (proactive nudge surface or
		// suggest_people_to_meet chat tool surface — `cardSurface` discriminates).
		MeetingPeopleCardClicked: 'Bizy Meeting People Card Clicked',
		// Data Agent chart export from the typed Chart node (`format`: csv | png).
		ChartExported: 'Bizy Chart Exported',
		// Data Agent table export — copy or CSV (`format`: copy | csv).
		TableExported: 'Bizy Table Exported',
		// One-tap follow-up suggestion clicked under a Data Agent answer.
		FollowUpClicked: 'Bizy Follow Up Clicked',
		// One-tap person card clicked to resolve an ambiguous name lookup.
		PersonPickerSelected: 'Bizy Person Picker Selected',
		// Drill into an evidence element (scorecard metric, chart bar, heatmap
		// cell, table row) — sends a scoped follow-up. `nodeType` discriminates.
		EvidenceDrilled: 'Bizy Evidence Drilled',
		// Recognition gif picker
		GifPickerOpened: 'Bizy Gif Picker Opened',
		GifPillClicked: 'Bizy Gif Pill Clicked',
		GifSelected: 'Bizy Gif Selected',
		// "Make it special" wand menu on the recognition draft card
		MakeItSpecialOpened: 'Bizy Make It Special Opened',
		DoodleStarted: 'Bizy Doodle Started',
		DoodleAttached: 'Bizy Doodle Attached',
		// Removing an attachment from the draft (gif or doodle — `kind` discriminates).
		AttachmentRemoved: 'Bizy Attachment Removed',
		// Post wrap (gift wrap) on the recognition draft
		PostWrapApplied: 'Bizy Post Wrap Applied',
		PostWrapRemoved: 'Bizy Post Wrap Removed',
		PostWrapBuyClicked: 'Bizy Post Wrap Buy Clicked',
		// User sorts a Data Agent table column.
		TableSorted: 'Bizy Table Sorted',
		// User toggles the "How I got this" source-details disclosure open/closed.
		SourceDetailsToggled: 'Bizy Source Details Toggled',
		// Member-access table on admin/company/settings/ai (all Bizy-enabled tiers).
		BizyAccessGranted: 'Bizy Access Granted',
		BizyAccessRevoked: 'Bizy Access Revoked',
		BizyAccessBulkGranted: 'Bizy Access Bulk Granted',
		BizyAccessBulkRevoked: 'Bizy Access Bulk Revoked',
		BizyAccessFilterChanged: 'Bizy Access Filter Changed',
		// What's New tips for admin users
		WhatsNewTipShown: 'Bizy Whats New Tip Shown',
		WhatsNewTipClicked: 'Bizy Whats New Tip Clicked',
		WhatsNewTipDismissed: 'Bizy Whats New Tip Dismissed',
		BizySkyHighInviteShown: 'Bizy Sky High Invite Shown',
		BizySkyHighInviteAction: 'Bizy Sky High Invite Action',
		BizySkyHighGameStarted: 'Bizy Sky High Game Started',
		BizySkyHighRunCompleted: 'Bizy Sky High Run Completed',
		BizySkyHighGameClosed: 'Bizy Sky High Game Closed',
		BizySkyHighSoundToggled: 'Bizy Sky High Sound Toggled',
	},

	/**
	 * Bizy Comment Assist Events (BIZY-583 epic).
	 * BeeShown/Tapped/GiveIdeas: BIZY-625. Suggestions*: BIZY-626.
	 * Coaching/MakeItShine/RefineChosen: BIZY-627. Posted funnel rollup: BIZY-628.
	 */
	CommentAssist: {
		BeeShown: 'Comment Assist Bee Shown',
		BeeTapped: 'Comment Assist Bee Tapped',
		GiveIdeasClicked: 'Comment Assist Give Ideas Clicked',
		SuggestionsShown: 'Comment Assist Suggestions Shown',
		SuggestionsRegenerated: 'Comment Assist Suggestions Regenerated',
		SuggestionsCleared: 'Comment Assist Suggestions Cleared',
		SuggestionChosen: 'Comment Assist Suggestion Chosen',
		CoachingHintShown: 'Comment Assist Coaching Hint Shown',
		MakeItShineClicked: 'Comment Assist Make It Shine Clicked',
		RefineSuggestionChosen: 'Comment Assist Refine Suggestion Chosen',
	},

	/**
	 * Bizy Profile Events
	 * Tracks interactions with the PersonalBizy profile fields surfaced in Bizy Settings.
	 * Profile field updates happen via Bizy chat; the settings UI only allows clearing
	 * a value, so only the clear/reject lifecycle is tracked from the FE.
	 */
	BizyProfile: {
		FieldCleared: 'Bizy Profile Field Cleared',
		FieldUpdateRejected: 'Bizy Profile Field Update Rejected',
	},

	/**
	 * Bizy Conversation History Events
	 * Tracks interactions on the user-facing Bizy conversation history page
	 */
	BizyHistory: {
		PageViewed: 'Bizy History Page Viewed',
		ConversationViewed: 'Bizy History Conversation Viewed',
		ConversationResumed: 'Bizy History Conversation Resumed',
		NewConversationStarted: 'Bizy History New Conversation Started',
		NavigatedFromWidget: 'Bizy History Navigated From Widget',
		NavigatedFromUserMenu: 'Bizy History Navigated From User Menu',
	},

	/**
	 * Admin Reports Events
	 * Tracks participation reports, managers/teams reports, and compare groups functionality
	 */
	AdminReports: {
		GivingAndReceivingReportView: 'Giving and Receiving Report View',
		ManagersAndTeamsReportView: 'Managers and Teams Report View',
		CompareGroupsReportView: 'Compare Groups Report View',
		CheckInsResultsReportView: 'Check-Ins Results Report View',
		RecognitionScoresReportView: 'Recognition Scores Report View',
		ParticipationReportExportClick: 'Participation Report Export Click',
		ParticipationReportDateChange: 'Participation Report Date Change',
		ParticipationReportSortChange: 'Participation Report Sort Change',
		ParticipationReportSearchChange: 'Participation Report Search Change',
		ManagersAndTeamsExpandRowClick: 'Managers and Teams Expand Row Click',
		GivingAndReceivingExpandParticipationTrendClick: 'Giving and Receiving Expand Participation Trend Click',
		GivingAndReceivingParticipationTrendHover: 'Giving and Receiving Participation Trend Hover',
		ManagersAndTeamsNotRecognizedCountHover: 'Managers and Teams Not Recognized Count Hover',
		ParticipationReportGroupChange: 'Participation Report Group Change',
		RecognizedByManagerTrendHover: 'Recognized by Manager Trend Hover',
		ExpandRecognizedByManagerTrendClick: 'Expand Recognized by Manager Trend Click',
		CompareGroupsFilterSearch: 'Compare Groups Filter Search',
		ExportCompareGroupsChart: 'Export Compare Groups Chart',
		ExportParticipationTrendChart: 'Export Participation Trend Chart',
		ExportRecognizedByManagerChart: 'Export Recognized by Manager Chart',

		// Report Email Subscriptions
		ReportSubscriptionsPageView: 'Report Subscriptions Page View',
		ReportEmailSubscriptionCreated: 'Report Email Subscription Created',
		ReportEmailSubscriptionDeleted: 'Report Email Subscription Deleted',
		ReportEmailSubscriptionUpdated: 'Report Email Subscription Updated',
		ReportEmailSubscriptionUnsubscribeAll: 'Report Email Subscription Unsubscribe All',

		// Pulse Results
		PulseResultsGetStartedClick: 'Pulse Results Get Started Click',
		PulseResultsTrendTopicChange: 'Pulse Results Trend Topic Change',
		PulseResultsGroupByChange: 'Pulse Results Group By Change',
		PulseResultsDateChange: 'Pulse Results Date Change',
		PulseResultsSettingsClick: 'Pulse Results Settings Click',

		// Recognition Scoring
		RecognitionScoresCompanyWideDateChange: 'Recognition Scores Company Wide Date Change',
		RecognitionScoresDeepDiveDateChange: 'Recognition Scores Deep Dive Date Change',
		RecognitionScoresGroupByChange: 'Recognition Scores Group By Change',
		RecognitionScoresAddFilter: 'Recognition Scores Add Filter',
		RecognitionScoresExportAll: 'Recognition Scores Export All',
		RecognitionScoresCompareDepartmentChange: 'Recognition Scores Compare Department Change',
		ReportedInaccurateTopRecognition: 'Admin Reported Inaccurate Top Recognition',

		// Points Activity Report
		PointsActivityReportView: 'Points Activity Report View',
		PointsActivityReportDateChange: 'Points Activity Report Date Change',
		PointsActivityReportCombinedExportClick: 'Points Activity Report Combined Export Click',
		PointsActivityReportExportClick: 'Points Activity Report Export Click',
		PointsActivityReportFilterChange: 'Points Activity Report Filter Change',
		PointsActivityReportGroupChange: 'Points Activity Report Group Change',
		PointsActivityReportRowClick: 'Points Activity Report Row Click',
		PointsActivityReportSearchChange: 'Points Activity Report Search Change',
		PointsActivityReportSortChange: 'Points Activity Report Sort Change',

		// Rewards Redemptions Report
		RewardsRedemptionsReportExportClick: 'Rewards Redemptions Report Export Click',

		// Recognition Activity Report
		RecognitionActivityReportView: 'Recognition Activity Report View',
		RecognitionActivityReportDateChange: 'Recognition Activity Report Date Change',
		RecognitionActivityReportExportClick: 'Recognition Activity Report Export Click',
		RecognitionActivityReportFilterChange: 'Recognition Activity Report Filter Change',
		RecognitionActivityReportSearchChange: 'Recognition Activity Report Search Change',
		RecognitionActivityReportSortChange: 'Recognition Activity Report Sort Change',
		RecognitionActivityReportTypeChange: 'Recognition Activity Report Type Change',
		RecognitionActivityReportDeleteAllBonuses: 'Recognition Activity Report Delete All Bonuses',

		// Admin Collaboration Map
		CollaborationMapView: 'Collaboration Map View',
		CollaborationMapDateChange: 'Collaboration Map Date Change',
		CollaborationMapGroupChange: 'Collaboration Map Group Change',
		CollaborationMapNodeCountChange: 'Collaboration Map Node Count Change',
		CollaborationMapSortChange: 'Collaboration Map Sort Change',
		CollaborationMapFilterApplied: 'Collaboration Map Filter Applied',
		CollaborationMapFilterCleared: 'Collaboration Map Filter Cleared',
		CollaborationMapDrillIn: 'Collaboration Map Drill In',
		CollaborationMapDrillOut: 'Collaboration Map Drill Out',
		CollaborationMapUserClick: 'Collaboration Map User Click',
		CollaborationMapArchetypeChange: 'Collaboration Map Archetype Change',
		CollaborationMapArchetypePaginate: 'Collaboration Map Archetype Paginate',
		CollaborationMapArchetypeExportClick: 'Collaboration Map Archetype Export Click',
		CollaborationMapViewModeChange: 'Collaboration Map View Mode Change',
	},

	/**
	 * Admin Reported Posts (Moderation) Events
	 * Tracks the recognition-moderation surface at admin/company/reported-posts:
	 * viewing the queue and resolving user reports / QualityBot deactivation
	 * reports. `resolution` carries the chosen outcome (e.g. delete_entirely,
	 * restore_giver) so the action's intent is legible downstream.
	 */
	AdminReportedPosts: {
		PageViewed: 'Admin Reported Posts Page Viewed',
		ReportResolved: 'Admin Reported Post Resolved', // resolution
		QualityBotReportResolved: 'Admin Quality Bot Report Resolved', // resolution
	},

	/**
	 * Admin Pages and Actions
	 */
	Admin: {
		OneOnOnesSummary: {
			EngagementPageView: 'Admin 1:1 Summary Engagement Page View',
			AdoptionPageView: 'Admin 1:1 Summary Adoption Page View',
			EngagementTableSort: 'Admin 1:1 Summary Engagement Table Sort', // sortBy, sortDirection
			EngagementUserProfileClick: 'Admin 1:1 Summary Engagement User Profile Click', // userId
			AdoptionTableSort: 'Admin 1:1 Summary Adoption Table Sort', // sortBy, sortDirection
			AdoptionUserProfileClick: 'Admin 1:1 Summary Adoption User Profile Click', // userId
			EngagementPagination: 'Admin 1:1 Summary Engagement Pagination', // pageNumber
			AdoptionPagination: 'Admin 1:1 Summary Adoption Pagination', // pageNumber
			AdoptionExportClick: 'Admin 1:1 Summary Adoption Export Click',
			EngagementExportClick: 'Admin 1:1 Summary Engagement Export Click',
			EngagementDateRangeChange: 'Admin 1:1 Summary Engagement Date Range Change', // startDate, endDate
		},
		CompanyInitiatives: {
			// List page
			PageView: 'Admin Company Initiatives Page View',
			ActiveTabClick: 'Admin Company Initiatives Active Tab Click',
			InactiveTabClick: 'Admin Company Initiatives Inactive Tab Click',
			CreateModalOpen: 'Admin Company Initiatives Create Modal Open', // source: 'header' | 'empty_state', tab
			EmptyStateCreateClick: 'Admin Company Initiatives Empty State Create Click', // tab
			InitiativeCreated: 'Admin Company Initiatives Initiative Created',
			GoToInitiative: 'Admin Company Initiatives Go To Initiative', // initiativeId, tab
			EditInitiativeClick: 'Admin Company Initiatives Edit Initiative Click', // initiativeId, tab
			InitiativeUpdated: 'Admin Company Initiatives Initiative Updated', // initiativeId
			InitiativeDeleted: 'Admin Company Initiatives Initiative Deleted', // initiativeId
			// Detail page
			DetailEditClick: 'Admin Company Initiatives Detail Edit Click', // initiativeId
			DetailDeleteConfirmed: 'Admin Company Initiatives Detail Delete Confirmed', // initiativeId
			DetailViewRelatedGoal: 'Admin Company Initiatives Detail View Related Goal', // initiativeId, goalId
		},
	},
	AdminTeamsAndGroups: {
		PageViewed: 'Admin Teams And Groups Page Viewed',
		TabViewed: 'Admin Teams And Groups Tab Viewed',
		CustomGroupCreated: 'Admin Teams And Groups Custom Group Created',
		CustomGroupUpdated: 'Admin Teams And Groups Custom Group Updated',
		CustomGroupDeleteConfirmed: 'Admin Teams And Groups Custom Group Delete Confirmed',
	},
	AdminExternalRecognition: {
		PageViewed: 'Admin External Recognition Page Viewed',
		TabViewed: 'Admin External Recognition Tab Viewed',
	},

	/**
	 * Feature Request Events
	 * Tracks when users click feature request/feedback buttons to open Bizy
	 */
	FeatureRequest: {
		ButtonClicked: 'Feature Request Button Clicked',
	},

	/**
	 * Bizy Prompt Button Events
	 * Tracks when users click contextual Bizy buttons placed on admin pages.
	 * The `source` property distinguishes the placement context.
	 */
	BizyPromptButton: {
		Clicked: 'Bizy Prompt Button Clicked',
	},

	/**
	 * Admin Nominations Events
	 * Tracks admin actions for nomination periods and nominees
	 */
	AdminNominations: {
		// Index page actions
		CreateNominationPeriod: 'Admin Create Nomination Period',
		SelectNominationPeriod: 'Admin Select a Nomination Period',
		ShareNominationPeriod: 'Admin Share Nomination Period',
		ReviewSubmissions: 'Admin Review Nomination Period Submissions',
		ExportCSV: 'Admin Nomination Period Export to CSV',
		EditNominationPeriod: 'Admin Edit Nomination Period',
		ArchiveNominationPeriod: 'Admin Archive Nomination Period',
		UnarchiveNominationPeriod: 'Admin Unarchive Nomination Period',
		DeleteNominationPeriod: 'Admin Delete Nomination Period',
		ReviewArchived: 'Admin Review Archived Nomination Periods',
		ArchivedPaginate: 'Admin Archived Nomination Periods Paginate',

		// Question editor actions
		QuestionImportCompanyValues: 'Admin Nominations Question Import Company Values',

		// Nomination Period detail actions
		DownloadCSV: 'Admin Download CSV',
		ShareResults: 'Admin Share Nomination Period Results',
		MakePublic: 'Admin Make Nomination Period Public',
		GiveAward: 'Admin Nomination Give Award',
		CreateAward: 'Admin Nomination Create Award',
		GiveRecognition: 'Admin Nomination Give Recognition',
		CopyMessages: 'Admin Nomination Copy Messages',

		// Voting results tab actions
		ViewVotingResults: 'Admin Nomination View Voting Results',
		ExportVotingResultsCSV: 'Admin Nomination Export Voting Results CSV',
		MarkWinner: 'Admin Nomination Mark Winner',
		UnmarkWinner: 'Admin Nomination Unmark Winner',
	},

	/**
	 * User Nominations Events
	 * Tracks user actions for submitting nominations
	 */
	UserNominations: {
		OpenNominationModal: 'User Nominate a User',
		SelectNominationPeriod: 'User Select a Nomination Period',
		SubmitNomination: 'User Submit Nomination',
	},

	/**
	 * Nominations Voting Events
	 * Tracks the user-facing voting page at /vote/nominations/:id
	 */
	NominationsVoting: {
		PageViewed: 'Nominations Voting Page Viewed',
		VoteToggled: 'Nominations Voting Vote Toggled',
		VotesSubmitted: 'Nominations Voting Votes Submitted',
		VotesSubmitFailed: 'Nominations Voting Votes Submit Failed',
		BannerViewed: 'Nominations Voting Banner Viewed',
		BannerClicked: 'Nominations Voting Banner Clicked',
		IndexPageViewed: 'Nominations Voting Index Page Viewed',
		IndexPeriodSelected: 'Nominations Voting Index Period Selected',
	},

	/**
	 * Admin Incentives Events
	 * Tracks admin actions for managing incentives and claims
	 */
	AdminIncentives: {
		// Page views
		SummaryReportView: 'Admin Incentives Summary Report View',

		// Index page actions
		CreateIncentive: 'Admin Index Create Incentive',
		SelectIncentive: 'Admin Select an Incentive',
		ReviewPosts: 'Admin Review Incentive Posts',
		IncentiveSummary: 'Admin Click Incentive Summary',
		EditIncentive: 'Admin Edit Incentive',
		ReviewArchivedIncentives: 'Admin Review Archived Incentives',
		ApproveIncentive: 'Admin Index Approve Incentive',
		ChoosePending: 'Admin Select Pending Incentive',
		IndexPaginate: 'Admin Index Paginate',
		SortIncentives: 'Admin Index Sort Incentives',
		PointsClicked: 'Admin Incentives Points Clicked',

		// Archived index page actions
		ArchivedSelectIncentive: 'Admin Archived Index Select Incentive',
		ArchivedEditIncentive: 'Admin Archived Index Edit Incentive',
		ArchivedDeleteIncentive: 'Admin Archived Index Delete Incentive',
		ArchivedPaginate: 'Admin Archived Index Paginate',
		ArchivedSortIncentives: 'Admin Archived Index Sort Incentives',
		ArchivedReviewPosts: 'Admin Archived Index Review Posts',

		// Modals
		DeactivateIncentive: 'Admin Deactivate Incentive',
		ReactivateIncentive: 'Admin Reactivate Incentive',
		DeleteIncentive: 'Admin Delete Incentive',

		// Summary report actions
		SummaryTimeframeChange: 'Admin Incentives Summary Timeframe Change',
		SummaryFilterIncentive: 'Admin Incentives Summary Filter Incentive',
		SummaryClearAll: 'Admin Incentives Summary Clear All',
		SummaryExportCsv: 'Admin Incentives Summary Export CSV',
		SummaryPaginate: 'Admin Incentives Summary Paginate',

		// Form actions
		RequireProof: 'Admin Incentive Form Require Proof Toggle',
		BasicInfoPreview: 'Admin Incentive Form Open Basic Info Preview',
		RecognitionPostPreview: 'Admin Incentive Form Open Recognition Post Preview',
		FilterAudienceButton: 'Admin Incentive Form Select Filter Audience Button',
		SetPointsBudget: 'Admin Incentive Form Set Points Budget',
		SetApprovalProcess: 'Admin Incentive Form Set Approval Process',

		// Email customization
		EmailCustomizationPreview: 'Admin Incentive Email Customization Preview',
		EmailCustomizationReset: 'Admin Incentive Email Customization Reset',
	},

	/**
	 * Admin Awards Events
	 * Tracks admin actions for managing awards
	 */
	AdminAwards: {
		// Index page actions
		CreateAward: 'Admin Index Create Award',
		SelectAward: 'Admin Select an Award',
		ReviewPosts: 'Admin Review Award Posts',
		AwardSummary: 'Admin Click Review Award Recognition Activity',
		EditAward: 'Admin Edit Award',
		ReviewArchivedAwards: 'Admin Review Archived Awards',
		ApproveAward: 'Admin Index Approve Award',
		ChoosePending: 'Admin Select Pending Award',
		IndexPaginate: 'Admin Awards Index Paginate',
		SortAwards: 'Admin Index Sort Awards',
		IndexDeactivateAward: 'Admin Index Deactivate Award Modal',
		BudgetUsedClicked: 'Admin Awards Budget Used Clicked',

		// Archived index page actions
		ArchivedSelectAward: 'Admin Archived Index Select Award',
		ArchivedEditAward: 'Admin Archived Index Edit Award',
		ArchivedDeleteAward: 'Admin Archived Index Delete Award Modal',
		ArchivedPaginate: 'Admin Archived Awards Index Paginate',
		ArchivedSortAwards: 'Admin Archived Index Sort Awards',
		ArchivedReviewPosts: 'Admin Archived AwardsIndex Review Posts',

		// Modals
		DeactivateAward: 'Admin Deactivate Award',
		ReactivateAward: 'Admin Reactivate Award',
		DeleteAward: 'Admin Delete Award',

		// Form actions
		RecognitionPostPreview: 'Admin Award Form Open Recognition Post Preview',
		SetPointsBudgetMode: 'Admin Award Form Set Points Budget',
		SetApprovalProcess: 'Admin Award Form Set Approval Process',
		ToggleVisibilityEditable: 'Admin Award Form Toggle Visibility Editable',
	},

	/**
	 * Awards Events (User)
	 * Tracks user interactions with giving awards
	 */
	Awards: {
		GiveboxButtonClicked: 'Givebox Award Button Clicked',
		GiveClick: 'Award Give Click',
		Selection: 'Award Selection',
		BackButton: 'Award Back Button',
		GiveCancel: 'Award Give Cancel',
		GiveComplete: 'Award Give Complete',
	},

	/**
	 * Rewards Events (User)
	 * Tracks user interactions with redeeming rewards
	 */
	Rewards: {
		// Reward browsing and navigation
		ClickRewardsLink: 'User Clicked Rewards Link',
		ClickRewardsCollection: 'User Clicked Rewards Collection',
		ClickCategoryFilter: 'User Clicked Category Filter',
		ClickViewReward: 'User Clicked View Reward',
		CloseRewardDetail: 'User Closed Reward Detail',
		ClickRewardPage: 'User Clicked Reward Page',

		// Reward details and redemption
		SelectRewardOption: 'User Changed Selected Reward Option',
		ClickPlaceOrder: 'User Clicked Place Order',
		ClickRedeemReward: 'User Clicked Redeem Reward',
		RewardRedemptionSuccess: 'User Succesfully Redeemed Reward',
		RewardRedemptionFailure: 'Reward Redemption Failed',
		PaymentIntentCreationFailure: 'Reward Payment Intent Creation Failed',

		// Collectible redemption
		PostWrapRedeemed: 'User Redeemed Post Wrap',

		// Falloff events
		AddressValidationFailed: 'User Shipping Address Validation Failed',
		UserBalanceInsufficient: 'User Balance Insufficient',
	},

	/**
	 * Announcements Events
	 * Tracks announcement creation, editing, viewing, and incentive claiming
	 */
	Announcements: {
		// User interaction
		AnnouncementCarouselInteraction: 'Announcement Carousel Interaction',

		// Create
		GiveboxAnnouncementButtonClicked: 'Givebox Announcement Button Clicked',
		MakeAnnouncementModalViewed: 'Make Announcement Modal Viewed',
		MakeAnnouncementModalClosed: 'Make Announcement Modal Closed',
		CreateAnnouncementRequestSubmitted: 'Create Announcement Request Submitted',
		CreateAnnouncementSuccess: 'Create Announcement Success',
		CreateAnnouncementFailure: 'Create Announcement Failure',

		// Edit
		EditAnnouncementButtonClicked: 'Edit Announcement Button Clicked',
		EditAnnouncementModalViewed: 'Edit Announcement Modal Viewed',
		EditAnnouncementModalClosed: 'Edit Announcement Modal Closed',
		ManageAnnouncementsButtonClicked: 'Manage Announcements Button Clicked',
		ManageAnnouncementsModalViewed: 'Manage Announcements Modal Viewed',
		ManageAnnouncementsModalClosed: 'Manage Announcements Modal Closed',
		UpdateAnnouncementRequestSubmitted: 'Update Announcement Request Submitted',
		UpdateAnnouncementSuccess: 'Update Announcement Success',
		UpdateAnnouncementFailure: 'Update Announcement Failure',

		// View
		ViewAnnouncementModalViewed: 'View Announcement Modal Viewed',
		ViewAnnouncementModalClosed: 'View Announcement Modal Closed',

		// Claim Incentive
		AnnouncementClaimIncentiveButtonClicked: 'Announcement Claim Incentive Button Clicked',
		ClaimIncentiveModalViewed: 'Claim Incentive Modal Viewed',
		ClaimIncentiveModalClosed: 'Claim Incentive Modal Closed',
		ClaimIncentiveRequestSubmitted: 'Claim Incentive Request Submitted',
		ClaimIncentiveSuccess: 'Claim Incentive Success',
		ClaimIncentiveFailure: 'Claim Incentive Failure',

		// Stack interactions (homepage)
		AnnouncementStackExpanded: 'Announcement Stack Expanded',
		AnnouncementCardClicked: 'Announcement Card Clicked',

		// Video attachment
		AttachVideoRequestSubmitted: 'Announcement Attach Video Request Submitted',
		AttachVideoSuccess: 'Announcement Attach Video Success',
		RemoveVideoButtonClicked: 'Announcement Remove Video Button Clicked',
	},

	/**
	 * Dynamic Promo Events
	 * Tracks interactions with the floating dynamic promo tile on the home page.
	 */
	DynamicPromo: {
		Viewed: 'Dynamic Promo Viewed',
		Dismissed: 'Dynamic Promo Dismissed',
		LearnMoreClicked: 'Dynamic Promo Learn More Clicked',
		CarouselNavigated: 'Dynamic Promo Carousel Navigated',
	},

	/**
	 * Incentives Events (User-facing)
	 * Tracks user interactions with incentives - viewing and claiming
	 */
	Incentives: {
		IncentivesPageViewed: 'Incentives Page Viewed',
		IncentiveDetailsPageViewed: 'Incentive Details Page Viewed',
		ClaimIncentiveModalViewed: 'Claim Incentive Modal Viewed',
		ClaimIncentiveModalClosed: 'Claim Incentive Modal Closed',
		ClaimIncentiveRequestSubmitted: 'Claim Incentive Request Submitted',
		ClaimIncentiveSuccess: 'Claim Incentive Success',
		ClaimIncentiveFailure: 'Claim Incentive Failure',
	},

	/**
	 * Tracker Widget Events
	 * Tracks user interactions with the tracker widget in the bonus feed
	 */
	TrackerWidget: {
		GiveFeedbackButtonClicked: 'Tracker Widget Give Feedback Button Clicked',
		GiveRecognitionButtonClicked: 'Tracker Widget Give Recognition Button Clicked',
		RequestFeedbackButtonClicked: 'Tracker Widget Request Feedback Button Clicked',
		TrackerWidgetHover: 'Tracker Widget Hover',
	},

	/**
	 * Top Navbar Events
	 * Tracks notifications and search interactions in the top navigation bar
	 */
	TopNavbar: {
		// Notifications
		NotificationsViewList: 'Notifications View List',
		NotificationAction: 'Notification Action',
		NotificationsMarkAllRead: 'Notifications Mark All Read',
		ClickFeedbackNotification: 'Click Feedback Notification',

		// Search - lifecycle
		SearchOpened: 'Monarch Search Opened',

		// Search - result clicks
		RecognitionClick: 'Monarch Search Recognition Click',
		UserClick: 'Monarch Search User Click',

		// Search - tab clicks
		AllTabClick: 'Monarch Search All Tab Click',
		UsersTabClick: 'Monarch Search Users Tab Click',
		RecognitionsTabClick: 'Monarch Search Recognitions Tab Click',
	},

	/**
	 * Navigation Customization Events
	 * Tracks company and user navbar customization actions
	 */
	Navigation: {
		CompanySettingsSaved: 'Navigation: Company settings saved',
		CompanySettingsReset: 'Navigation: Company settings reset',
		UserPreferencesSaved: 'Navigation: User preferences saved',
		UserPreferencesReset: 'Navigation: User preferences reset',
		MoreMenuOpened: 'Navigation: More menu opened',
	},

	/**
	 * User Navbar Events
	 * Tracks user interactions with the user navbar
	 */
	UserNavbar: {
		AnnouncementsNavigation: 'User Navbar Announcements Navigation Clicked',
		SearchNavigation: 'User Navbar Search Navigation Clicked',
		HomeNavigation: 'User Navbar Home Navigation Clicked',
		OneOnOnesNavigation: 'User Navbar One On Ones Navigation Clicked',
		GoalsNavigation: 'User Navbar Goals Navigation Clicked',
		RecapsNavigation: 'User Navbar Recaps Navigation Clicked',
		TeamNavigation: 'User Navbar Team Navigation Clicked',
		RewardsNavigation: 'User Navbar Rewards Navigation Clicked',
		IncentivesNavigation: 'User Navbar Incentives Navigation Clicked',
		LeaderboardNavigation: 'User Navbar Leaderboard Navigation Clicked',
		LeaderboardSummaryExportClick: 'User Navbar Leaderboard Summary Export Clicked',
		CelebrationsNavigation: 'User Navbar Celebrations Navigation Clicked',
		AdviceNavigation: 'User Navbar Advice Navigation Clicked',
		CustomizeSidebarOpened: 'User Navbar Customize Sidebar Opened',
		SidebarItemToggled: 'User Navbar Sidebar Item Toggled',
		CreateNewMenuNavigation: 'User Navbar Create New Menu Clicked',
		AdminNavigation: 'User Navbar Admin Navigation Clicked',
		LabsNavigation: 'User Navbar Labs Navigation Clicked',
		UserNavigation: 'User Navbar User Navigation Clicked',
		ViewProfileNavigation: 'User Navbar View Profile Navigation Clicked',
		ProfileSettingsNavigation: 'User Navbar Profile Settings Navigation Clicked',
		BookmarksNavigation: 'User Navbar Bookmarks Navigation Clicked',
		NotificationsNavigation: 'User Navbar Notifications Navigation Clicked',
	},

	/**
	 * Team Panel Events
	 * Tracks user interactions with the team panel
	 */
	TeamPanel: {
		LinkClicked: 'Team Panel Link Clicked', // link: Team Hub, Pulse Results, Team Report, Team Member
		DirectReportActionMenuOpened: 'Team Panel Direct Report Action Menu Opened', // directReportId
		DirectReportActionClicked: 'Team Panel Direct Report Action Clicked', // directReportId, action: Give Recognition, Create Goal, Share Advice, Create Custom Recap
	},

	/**
	 * MS Teams Events
	 * Tracks user interactions within the Microsoft Teams integration
	 */
	MSTeams: {
		DoMoreButtonClick: '[MsTeams] Clicked Do More Button',
		ChannelClick: '[MsTeams] Clicked Channel',
		GiveRecognitionClick: '[MsTeams] Give action triggered',
		ShopRewardsClick: '[MsTeams] Clicked Shop Reward',
		RedeemPointBoostClick: '[MsTeams] Clicked Redeem Point Boost',
	},

	/**
	 * Invite Users Modal Events
	 * Tracks when users send invitations through the invite modal
	 */
	InviteModal: {
		OpenOnPageLoad: 'Users invite: Open invitation modal on page load',
		OpenModal: 'Users invite: Open invitation modal',
		SendSuccess: 'Users invite: Send invitations (success)',
		SendFail: 'Users invite: Send invitations (fail)',
		InsertEmail: 'Users invite: Insert email',
		InsertMessage: 'Users invite: Insert message',
		Cancel: 'Invite Users Modal Cancel',
		InvitationSent: 'Invite Users Modal Invitation Sent',
	},

	/**
	 * Onboarding Events
	 * Tracks user interactions with onboarding modals and flows
	 */
	Onboarding: {
		// Welcome page (signup flow)
		WelcomeFormViewed: 'Onboarding Welcome Form Viewed',
		WelcomeFormCompleted: 'Onboarding Welcome Form Completed',
		WelcomeFormFailure: 'Onboarding Welcome Form Failure',
		WelcomeV2StepCompleted: 'Onboarding Welcome V2 Step Completed',

		// Bizy-led founding-admin onboarding (Welcome V2)
		// Stage 1: conversational chat steps
		AdminStarted: 'Onboarding Admin Started',
		AdminChatStepCompleted: 'Onboarding Admin Chat Step Completed',
		AdminChatStepSkipped: 'Onboarding Admin Chat Step Skipped',
		AdminChatDropped: 'Onboarding Admin Chat Dropped',
		AdminCompleted: 'Onboarding Admin Completed',
		// Stage 2: intent chips (goals step)
		AdminIntentChipsShown: 'Onboarding Admin Intent Chips Shown',
		AdminIntentChipSelected: 'Onboarding Admin Intent Chip Selected',
		AdminIntentChipSkipped: 'Onboarding Admin Intent Chip Skipped',
		AdminIntentChipChanged: 'Onboarding Admin Intent Chip Changed',

		// Stage 3: demo recognition (RR-132)
		AdminDemoRecognitionStarted: 'Onboarding Admin Demo Recognition Started',
		AdminDemoRecognitionCompleted: 'Onboarding Admin Demo Recognition Completed',
		AdminDemoRecognitionDismissed: 'Onboarding Admin Demo Recognition Dismissed',
		AdminDemoRecognitionUnavailable: 'Onboarding Admin Demo Recognition Unavailable',

		// Homepage getting-started cards
		GettingStartedCardClicked: 'Onboarding Getting Started Card Clicked',

		// Stage 4: guided first redemption (RR-72)
		RedeemOfferShown: 'Onboarding Redeem Offer Shown',
		RedeemOfferAccepted: 'Onboarding Redeem Offer Accepted',
		RedeemOfferDismissed: 'Onboarding Redeem Offer Dismissed',
		RedeemDemoStarted: 'Onboarding Redeem Demo Started',
		RedeemDemoCompleted: 'Onboarding Redeem Demo Completed',

		// Review solicitations modal
		ReviewSolicitationsModalViewed: 'Review Solicitations Modal Viewed',
		ReviewSolicitationsModalDismissed: 'Review Solicitations Modal Dismissed',
		ReviewSolicitationsModalRemindLaterClicked: 'Review Solicitations Modal Remind Later Clicked',
		ReviewSolicitationsModalStartReviewClicked: 'Review Solicitations Modal Start Review Clicked',
	},

	/**
	 * Onboarding Trial Banner Events
	 * Tracks user interactions with the trial banner
	 */
	OnboardingTrialBanner: {
		ManagePlanClicked: 'Onboarding Trial Banner Manage Plan Clicked',
		ManagePlanClickedOld: 'Trial Banner: Manage plan clicked',
	},

	/**
	 * Trial "What's Next?" Events (RR-75)
	 * Tracks interactions with the end-of-trial recommendation page and its
	 * auto-presented interstitial.
	 */
	TrialWhatsNext: {
		Viewed: 'Trial Whats Next Viewed',
		AutoPresented: 'Trial Whats Next Auto Presented',
		Dismissed: 'Trial Whats Next Dismissed',
		ContinueOnTeamClicked: 'Trial Whats Next Continue On Team Clicked',
		SwitchToFreeClicked: 'Trial Whats Next Switch To Free Clicked',
	},

	/**
	 * Feedback Events
	 * Tracks user interactions with the feedback system
	 */
	Feedback: {
		ChangeFilter: 'Change Feedback Filter',
		EditFeedbackDraftButtonClicked: 'Edit Feedback Draft Button Clicked',
		GiveFromRecognition: 'Give Feedback From Recognition',
		ManageUnrequestedFeedbackModalClosed: 'Manage Unrequested Feedback Modal Closed',
		ManageUnrequestedFeedbackModalViewed: 'Manage Unrequested Feedback Modal Viewed',
		Navigate: 'Navigate to Feedback',
		RequestFeedback: 'Click Request Feedback',
		RequestFeedbackModalOpened: 'Request Feedback Modal Opened',
		RequestFeedbackDraftSaved: 'Request Feedback Draft Saved',
		RequestFromRecognition: 'Request Feedback From Recognition',
		DraftFromTemplateCreated: 'Template-Based Draft Feedback Created',
		RespondToRequest: 'Click Respond to Feedback Request',
		SendRequest: 'Click Send Feedback Request',
		SendResponse: 'Click Send Feedback Response',
		UnsolicitedCreated: 'unsolicited-feedback-created',
		UnsolicitedFeedbackAddResponse: 'Unsolicited Feedback Add Response Clicked',
		UnsolicitedFeedbackDraftSaved: 'Unsolicited Feedback Draft Saved',
		UnsolicitedFeedbackModalOpened: 'Unsolicited Feedback Modal Opened',
		UnsolicitedFeedbackSent: 'Unsolicited Feedback Sent',
		SolicitedFeedbackDraftSaved: 'Solicited Feedback Draft Saved',
		UpdateRequest: 'Click Edit Feedback Request',
		PendingFeedbackRespondButtonClicked: 'Pending Feedback Respond Button Clicked',

		GiveButtonClicked: 'Give Feedback Button Clicked',
		AskButtonClicked: 'Ask For Feedback Button Clicked',
	},

	/**
	 * Recognition Events (Bonus Feed)
	 * Tracks recognition-related interactions in the bonus feed
	 */
	Recognition: {
		// Comment box interactions
		CommentStart: 'Monarch Comment Box Start',
		CommentSubmit: 'Monarch Comment Box Submit',
		CommentAdded: 'Comment Added',

		// View changes
		ViewChange: 'Monarch Bonus Feed View Change',

		// Suggested thank you
		SuggestedThankYouShown: 'Suggested Thank You Shown',
		SuggestedThankYouClicked: 'Suggested Thank You Clicked',
		SuggestedThankYouDismissed: 'Suggested Thank You Dismissed',

		// Stronger connections widget
		StrongerConnectionsFollowUser: 'Stronger Connections Widget - Follow User',
		StrongerConnectionsRecognizeUser: 'Stronger Connections Widget - Recognize User',

		// Reaction actions
		ReactionCreateAction: 'Monarch Bonus Feed Reaction Create Action',
		ReactionReplaceAction: 'Monarch Bonus Feed Reaction Replace Action',
		ReactionDeleteAction: 'Monarch Bonus Feed Reaction Delete Action',

		// Employee Spotlight
		SpotlightClicked: 'Employee Spotlight Clicked', // spotlightId: string, viewerIsAdmin: 'true' | 'false', viewerIsManager: 'true' | 'false', parentId: string, parentType: 'Bonus' | 'Comment'
	},

	/**
	 * Give Box Events
	 * Tracks recognition giving interactions in the give box
	 */
	GiveBox: {
		BonusStart: 'Monarch Giving Box Start',
		BonusSubmit: 'Monarch Giving Box Submit',
		RecognitionGiven: 'Recognition Given',
		RecognitionGivenWithGroups: 'Recognition Given with Groups',
		GroupSelected: 'Give Box Group selected',
	},

	/**
	 * Invite By Email Events (RR-267)
	 * Tracks the invite-by-email confirmation step in the give flow.
	 * Completion is covered by `hasInviteEmails` / `inviteEmailCount` props
	 * on GiveBox.BonusSubmit / GiveBox.RecognitionGiven.
	 */
	InviteByEmail: {
		ConfirmationShown: 'Invite By Email Confirmation Shown',
		ConfirmationCancelled: 'Invite By Email Confirmation Cancelled',
	},

	/**
	 * Celebrations Events
	 * Tracks user interactions with celebration widgets
	 */
	Celebrations: {
		CelebrationLinkClick: 'Celebration Widget Link Click',
		CelebrationTooltipHover: 'Celebration Widget Tooltip Hover',
		CelebrationModalLinkClick: 'Celebration Modal Link Click',
		OpenCelebrationsModal: 'Open Celebrations Modal Button Click',
		CommentCreated: 'Celebration Comment Created',
	},

	/**
	 * Admin Celebrations Events
	 * Tracks admin celebration management actions
	 */
	AdminCelebrations: {
		Create: 'Create Celebration',
		CreateFailure: 'Create Celebration Failure',
		Deactivate: 'Deactivate Celebration',
		Reactivate: 'Reactivate Celebration',
		Reset: 'Reset Celebration to Default',
		Review: 'Review Celebration',
		View: 'View Celebration Settings',
		Edit: 'Edit Celebration',
		EditFailure: 'Edit Celebration Failure',
		OpenCreateModal: 'Open Create Celebration Modal',
		DownloadCSV: 'Download Celebrations CSV',
		ViewHRISConnections: 'View HRIS Connections from Celebrations',
		PointsClicked: 'Admin Celebrations Points Clicked',
	},

	/**
	 * Video Recording Events
	 * Tracks video recording and playback interactions
	 */
	Video: {
		// Recording
		RecordingModalOpen: 'video-open-recording-modal',
		RecordStart: 'video-record-start',
		AttachToBonus: 'video-attach-to-bonus',
		RecordingModalClose: 'video-recording-abandoned',

		// Playback
		BonusUnmute: 'video-bonus-unmute',
		BonusPlay: 'video-bonus-play',
		BonusPause: 'video-bonus-pause',
		BonusEnd: 'video-bonus-end',
		BonusView: 'video-bonus-view',

		SpeechRecognition: 'Video Speech Recognition',
	},

	/**
	 * Developer Tooling Events
	 * Tracks internal support and debugging tools
	 */
	Developer: {
		BugRecordingStarted: 'Bug Recording Started',
		BugRecordingStopClicked: 'Bug Recording Stop Clicked',
	},

	/**
	 * Analytics Data Components Events
	 * Tracks interactions with analytics dashboards and data visualization
	 */
	AnalyticsData: {
		// Page views
		ViewedRecognitionReceivedPage: 'Viewed Recognition Received Page',
		ViewedRecognitionRatesPage: 'Viewed Recognition Rates Page',
		ViewedRecognitionHashtagPage: 'Viewed Recognition Hashtag Page',
		ViewedRecognitionAddonsPage: 'Viewed Recognition Addons Page',
		ViewedCompareRatesPage: 'Viewed Compare Rates Page',
		ViewedManagersTeamSegment: 'Viewed Managers Team Segment',
		PageVisit: 'Page Visit',

		// Chart interactions
		ExportRecognitionChart: 'Export Recognition Chart',
		CompareEmployeeSegmentsLink: 'Compare Employee Segments Link',

		// Widget interactions
		StrongerConnectionsAvatarClicked: 'Stronger Connections Widget - Click on User Avatar Profile',
		StrongerConnectionsAvatarHovered: 'Stronger Connections Widget - Hovered on User Profile',
		StrongerConnectionsViewMore: 'Stronger Connections Widget - View More Connections',
		ManagerWidgetAvatarClicked: 'Manager Widget - Click on User Profile',
	},

	/**
	 * Bonus Feed / Custom Feed Events
	 * Tracks custom feed interactions
	 */
	BonusFeed: {
		CustomFeedAdded: 'Custom Feed Added',
		CustomFeedRemoved: 'Custom Feed Removed',
		CustomFeedVisited: 'Custom Feed Visited',
		RefreshFeed: 'Refresh Bonus Feed',
	},

	/**
	 * Admin Email Customization Events
	 * Tracks email customization interactions
	 */
	AdminEmailCustomization: {
		ResetButtonClicked: 'Email Customizations Reset Button Clicked',
		PreviewButtonClicked: 'Email Customizations Preview Button Clicked',
		WelcomeEmailTextChanged: 'Email Customizations Welcome Email Text Changed',
		InviteEmailTextChanged: 'Email Customizations Invite Email Text Changed',
		UseCompanyLogoChanged: 'Email Customizations Use Company Logo Changed',
	},

	/**
	 * Admin Company Security Events
	 * Tracks the admin-initiated MFA reset flow in Company Account Settings > Security
	 */
	AdminCompanySecurity: {
		MfaResetModalOpened: 'Admin Company Security MFA Reset Modal Opened',
		MfaResetSubmitted: 'Admin Company Security MFA Reset Submitted',
		MfaResetSucceeded: 'Admin Company Security MFA Reset Succeeded',
		MfaResetFailed: 'Admin Company Security MFA Reset Failed',
	},

	/**
	 * Admin Custom Rewards Events
	 * Tracks custom reward management
	 */
	AdminCustomRewards: {
		AddRewardClick: 'Admin Reward Catalog Add Reward Click',
		CreateNewClick: 'Admin Custom Reward Create New Click',
		ConfirmDeleteClick: 'Admin Custom Reward Confirm Delete Click',
		CountryClick: 'Admin Reward Catalog Country Click',
		DeleteClick: 'Admin Custom Reward Delete Click',
		EditClick: 'Admin Custom Reward Edit Click',
		PublishNewClick: 'Admin Custom Reward Publish New Click',
		PublishUpdatesClick: 'Admin Custom Reward Publish Updates Click',
		RemoveRewardClick: 'Admin Reward Catalog Remove Reward Click',
		StatusToggle: 'Admin Custom Reward Status Toggle',
		PromotedRewardsSettingUpdated: 'Promoted Rewards Setting Updated',
		ManagePromotedRewardsButtonClicked: 'Manage Promoted Rewards Button Clicked',
	},

	/**
	 * Flash Points Events
	 * Tracks flash points program interactions
	 */
	FlashPoints: {
		// User events
		FullBalanceUsed: 'Flash Points Full Balance Used',

		// Admin events
		ProgramCreated: 'Flash Points Program Created',
		ProgramUpdated: 'Flash Points Program Updated',
		ProgramDeleted: 'Flash Points Program Deleted',
		ProgramEndedEarly: 'Flash Points Program Ended Early',
	},

	/**
	 * Draft Assist Events
	 * Tracks AI-powered draft suggestion interactions
	 */
	DraftAssist: {
		SuggestionShown: 'Draft Assist: Suggestion Shown',
		SuggestionAccepted: 'Draft Assist: Suggestion Accepted',
		SuggestionDismissed: 'Draft Assist: Suggestion Dismissed',
		SessionStarted: 'Draft Assist: Session Started',
		OutcomeSignaled: 'Draft Assist: Outcome Signaled',
		SuggestionEditedAfterAccept: 'Draft Assist: Suggestion Edited After Accept',
		// Per-session aggregate of accepted-suggestion retention. Length-weighted across all
		// accepts in the session so a longer accepted suggestion influences the ratio more
		// than a shorter one.
		SessionSuggestionRetention: 'Draft Assist: Session Suggestion Retention',
	},

	/**
	 * Gratitude Gauge Events
	 * Tracks gratitude gauge interactions (AI quality scoring)
	 */
	GratitudeGauge: {
		Opened: 'User Opened Gratitude Gauge',
		Closed: 'User Closed Gratitude Gauge',
		FeedbackProvided: 'User Provided Gratitude Gauge Feedback',
		DimensionDetailClicked: 'User Clicked Dimension Detail',
		ViewDimensionExamples: 'User Clicked View Dimension Examples',
		ScoredRecognitionDraft: 'User Scored Recognition Draft',
		DraftAssistHintShown: 'Draft Assist Hint Shown',
		DraftAssistHintClicked: 'Draft Assist Hint Clicked',
	},

	/**
	 * One-on-One Meeting Events
	 * Tracks one-on-one meeting interactions and AI features
	 */
	OneOnOne: {
		// Page viewing
		IndexPageViewed: 'One on One Index Page Viewed',

		// Meeting viewing
		MeetingViewed: 'One on One Meeting Viewed',
		EndMeetingButtonClicked: 'One on One End Meeting Button Clicked',

		// Agenda & Topics
		TopicAdded: 'One on One Topic Added',
		AgendaTopicInteraction: 'Meeting Agenda Topic Interaction',
		AgendaTopicReordered: 'Meeting Agenda Topic Reordered',
		AgendaTopicDeleted: 'Meeting Agenda Topic Deleted',

		// Empty notes actions
		UseTemplateClicked: 'One on One Use Template Clicked',
		AskBizyClicked: 'One on One Ask Bizy Clicked',

		// Agenda Topic Menu Actions (trackClick)
		AgendaTopicMenuRepeatClicked: 'Meeting Agenda Topic Menu Repeat Clicked',
		AgendaTopicMenuOpenDetailsClicked: 'Meeting Agenda Topic Menu Open Details Clicked',
		AgendaTopicMenuSearchHistoryClicked: 'Meeting Agenda Topic Menu Search History Clicked',
		AgendaTopicDetailsClosed: 'Meeting Agenda Topic Details Closed',
		AgendaAddTopicClicked: 'Meeting Agenda Add Topic Clicked',
		AgendaAddGoalClicked: 'Meeting Agenda Add Goal Clicked',

		// AI Suggestions
		SmartSuggestionClicked: 'Summary Smart Suggestion Clicked',

		// Transcription
		TranscriptionAddClicked: 'Meeting Transcription Add Clicked',
		TranscriptionUploadClicked: 'Meeting Transcription Upload Clicked',
		TranscriptionSummarizeClicked: 'Meeting Transcription Summarize Clicked',
		TranscriptionStartClicked: 'One on One Transcription Start Clicked',
		TranscriptionResumeClicked: 'One on One Transcription Resume Clicked',
		TranscriptionStopClicked: 'One on One Transcription Stop Clicked',

		// Invitations
		UsersInviteInsertEmail: 'Users invite: Insert email',
		UsersInviteInsertMessage: 'Users invite: Insert message',
		UsersInviteSendFail: 'Users invite: Send invitations (fail)',

		// Mic Permission Recovery (HABITS-582)
		MicBlockedModalOpened: 'One on One Mic Blocked Modal Opened',
		MicBlockedTryAgainClicked: 'One on One Mic Blocked Try Again Clicked',
		MicBlockedUploadTranscriptClicked: 'One on One Mic Blocked Upload Transcript Clicked',
		MicBlockedPermissionGranted: 'One on One Mic Blocked Permission Granted',

		// Meeting Presence State (engagement funnel instrumentation)
		MeetingSoloPresence: 'One on One Meeting Solo Presence',
		MeetingBothPresent: 'One on One Meeting Both Present',
		MeetingSoloRecording: 'One on One Meeting Solo Recording',
		MeetingBothRecording: 'One on One Meeting Both Recording',

		// Meeting Timing (engagement funnel instrumentation)
		MeetingPreparing: 'One on One Meeting Preparing',
		MeetingHolding: 'One on One Meeting Holding',

		// Recording Request (HABITS-582)
		RecordingRequestModalShown: 'One on One Recording Request Modal Shown',
		RecordingRequestModalDismissed: 'One on One Recording Request Modal Dismissed',
		RecordingRequestNudgeClicked: 'One on One Recording Request Nudge Clicked',
		TranscriptionAutoJoined: 'One on One Transcription Auto Joined',

		// Create 1:1 Modal (V2 flow)
		CreateModalOpened: 'One on One Create Modal Opened',
		CreateModalTabChanged: 'One on One Create Modal Tab Changed',

		// Calendar Connection (V2 flow)
		CalendarConnectClicked: 'One on One Calendar Connect Clicked',
		CalendarConnected: 'One on One Calendar Connected',
		CalendarUserSelected: 'One on One Calendar User Selected',

		// Colleague Search (V2 flow)
		ColleagueSearched: 'One on One Colleague Searched',

		// Creation Outcomes (V2 flow)
		OneOnOneCreated: 'One on One Created',
		ExistingOneOnOneViewed: 'One on One Existing Viewed',

		// Meeting Satisfaction Feedback
		MeetingFeedbackShown: 'One on One Meeting Feedback Shown',
		MeetingFeedbackSubmitted: 'One on One Meeting Feedback Submitted',
		MeetingFeedbackSkipped: 'One on One Meeting Feedback Skipped',

		// Onboarding Tour
		OnboardingTourStarted: 'One on One Onboarding Tour Started',
		OnboardingTourDismissedWithoutViewing: 'One on One Onboarding Tour Dismissed Without Viewing',

		// Join Nudge (Slack/Teams invite)
		JoinNudgeSent: 'One on One Join Nudge Sent', // meetingId, channel

		// Transcription Active Modal (shown when joining meeting with active recording)
		TranscriptionActiveModalShown: 'One on One Transcription Active Modal Shown',
		TranscriptionActiveModalDismissed: 'One on One Transcription Active Modal Dismissed',

		// Navigation Blocker (shown during active transcription)
		NavigationBlockerShown: 'One on One Navigation Blocker Shown',
		NavigationBlockerStayClicked: 'One on One Navigation Blocker Stay Clicked',
		NavigationBlockerLeaveClicked: 'One on One Navigation Blocker Leave Clicked',

		// Agenda prefill
		AgendaPrefillAttempted: 'One on One Agenda Prefill Attempted',

		// Email CTA suggestion banner (explicit meeting creation flow)
		EmailCtaBannerShown: 'One on One Email CTA Banner Shown',
		EmailCtaStartClicked: 'One on One Email CTA Start Clicked',
		EmailCtaMeetingCreated: 'One on One Email CTA Meeting Created',

		// Meeting Tags (e.g. development_conversation)
		TagToggled: '1:1 Meeting Tag Toggled', // meetingId, tag, action: 'added' | 'removed'
	},

	/**
	 * Check-Ins Events
	 * Tracks check-in interactions and settings
	 */
	CheckIns: {
		// Modal interactions
		ModalOpened: 'Check-In Modal Opened',
		ScoreSelected: 'Check-In Score Selected',
		CheckInSubmitted: 'Check-In Submitted',
		EditClicked: 'Check-In Edit Clicked',
		HistoryButtonClicked: 'Check-In History Button Clicked',
		LoadMoreTriggered: 'Check-In Load More Triggered',
		SetUpOneOnOneClicked: 'Check-In Set Up 1-1 Button Clicked',

		// Meeting check-in interactions
		MeetingCheckInOpenDetailsClicked: 'Meeting Check-in Open Details Clicked',
		ShareRecentNonMeetingCheckInClicked: 'Share recent non-meeting check-in clicked',
		MeetingCheckInDetailsAddClicked: 'Meeting Check-in Details Add Clicked',
		MeetingCheckInDetailsSendClicked: 'Meeting Check-in Details Send Clicked',
		MeetingCheckInScoreSelected: 'Meeting Check-in Score Selected',
		MeetingCheckInDetailsEditClicked: 'Meeting Check-in Details Edit Clicked',

		// Check-Ins Over Time Widget
		OverTimeWidgetViewed: 'Check-Ins Over Time Widget Viewed',
		OverTimeTimePeriodSelected: 'Check-Ins Over Time Widget Time Period Selected',
		OverTimeCheckInExpanded: 'Check-Ins Over Time Widget Check-In Expanded',
		OverTimeChartColumnSelected: 'Check-Ins Over Time Widget Chart Column Selected',

		ManagerCheckInSettingsModal: {
			ModalOpened: 'Manager Check-In Settings Modal Opened',
			SettingsUpdated: 'Manager Check-In Settings Updated',
			ConfirmStopClicked: 'Manager Check-In Confirm Stop Clicked',
		},

		AdminCheckInSettingsForm: {
			SettingsUpdated: 'Admin Check-In Settings Updated',
			AnnouncementModalOpened: 'Admin Check-In Announcement Modal Opened',
			ConfirmDisableClicked: 'Admin Check-In Confirm Disable Clicked',
		},
	},

	/**
	 * Team Hub Events
	 * Tracks team hub, pulse results and team report page views and interactions
	 */
	TeamHub: {
		YourTeamView: 'Team Hub Your Team Page View',
		CheckInsResultsReportView: 'Team Hub Check-Ins Results Report View',
		ReportView: 'Team Hub Report View',
		SetupPulseCheckClicked: 'Team Hub Setup Pulse Check Button Clicked',
		PulseCheckTabViewed: 'Team Management Pulse Check Tab Viewed',
		OverviewTableSortChanged: 'Team Management Overview Table Sort Changed',
		RecognitionStatsGetStartedClick: 'Team Hub Recognition Stats Get Started Clicked',
		OneOnOneStatsGetStartedClick: 'Team Hub One On One Stats Get Started Clicked',
		PulseResultsGetStartedClick: 'Team Hub Pulse Results Get Started Clicked',
		PulseTrendsDateChanged: 'Team Hub Pulse Trends Date Changed',
		DirectTeamTabClicked: 'Team Hub Team Report Direct Team Tab Clicked',
		ExtendedTeamTabClicked: 'Team Hub Team Report Extended Team Tab Clicked',
		TeamReportDateChanged: 'Team Hub Team Report Date Changed',
		TeamReportPulseTrendHovered: 'Team Report Direct Team Pulse Check Trend Hovered',
		TeamReportRecognitionTabClicked: 'Team Hub Team Report Extended Team Recognition Tab Clicked',
		TeamReportPulseCheckTabClicked: 'Team Hub Team Report Extended Team Pulse Check Tab Clicked',
		ExtendedTeamPulseTrendHovered: 'Team Report Extended Team Pulse Check Trend Hovered',
		ExtendedTeamRecognitionTrendHovered: 'Team Report Extended Team Recognition Trend Hovered',
	},

	/**
	 * Team Member Page Events
	 * Tracks team member page interactions for direct reports
	 */
	TeamMemberPage: {
		NavigationClick: 'Team Member Page Navigation Click', // link: 1:1s, Goals, Advice, Recaps, directReportId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		OneOnOnePanelTabChanged: 'Team Member Page 1:1 Panel Tab Changed', // tab: ask, history, info, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		OneOnOnePanelCollapsed: 'Team Member Page 1:1 Panel Collapsed', // managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		OneOnOneMeetingButtonClick: 'Team Member Page 1:1 Meeting Button Click', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingRegenerateSummaryClick: 'Team Member Page Regenerate Meeting Summary', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingTabNavigation: 'Team Member Page Completed Meeting Navigation', // tab: summary, notes, transcript, participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingCopyTranscriptClick: 'Team Member Page Completed Meeting Copy Transcript', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingDownloadTranscriptClick: 'Team Member Page Completed Meeting Download Transcript', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingEditTranscriptClick: 'Team Member Page Completed Meeting Edit Transcript', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingEditNotesClick: 'Team Member Page Completed Meeting Edit Notes', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
		CompletedMeetingDoneEditingNotesClick: 'Team Member Page Completed Meeting Done Editing Notes', // participantId: string, meetingId: string, managerViewingDirectReportMeeting: 'true' | 'false' | 'unknown'
	},

	/**
	 * Growth / Performance Management Events
	 * Tracks review recap interactions
	 */
	Growth: {
		PrintRecapReviewViewed: 'Print Recap Review Viewed',
	},

	/**
	 * Spotlight Events
	 * Tracks spotlight viewing and interactions
	 */
	Spotlight: {
		EntryViewed: 'Spotlight Entry Viewed',
		ListOpened: 'Spotlight List Opened',
		GeneratorOpened: 'Spotlight Generator Opened',
		GenerationStarted: 'Spotlight Generation Started',
		TimeframeAdjusted: 'Spotlight Timeframe Adjusted',
		ScrollDepth: 'Spotlight Scroll Depth',
		RecognitionCtaClicked: 'Spotlight Recognition CTA Clicked',
		SectionEditClicked: 'Spotlight Section Edit Clicked',
		SectionEdited: 'Spotlight Section Edited',
		SectionDeleted: 'Spotlight Section Deleted',
		WorkAnniversaryCTAClicked: 'Spotlight Work Anniversary CTA Clicked', // receiverId: string
		WorkAnniversaryModalGenerateClicked: 'Spotlight Work Anniversary CTA Generate Clicked', // receiverId: string
		MessagingBannerCtaClicked: 'Spotlight Messaging Banner CTA Clicked',
		MessagingBannerSampleClicked: 'Spotlight Messaging Banner Sample Clicked',
		MessagingBannerDismissed: 'Spotlight Messaging Banner Dismissed',
		GiveBoxActionClicked: 'Spotlight GiveBox Action Clicked',
	},

	/**
	 * Recaps Events
	 * Tracks recaps interactions
	 */
	Recaps: {
		CreateRecapCTAClicked: 'Team Member Page Create Recap CTA Clicked', // recapUserId: string, managerCreatingDirectReportRecap: 'true' | 'false'
		GenerateRecapClicked: 'Team Member Page Generate Recap Clicked', // recapUserId: string, managerCreatingDirectReportRecap: 'true' | 'false'
		ViewRecapClicked: 'Team Member Page View Recap Clicked', // recapUserId: string, managerViewingDirectReportRecap: 'true' | 'false'
		PageViewed: 'Recaps & spotlights page view', // tabType: 'recaps' | 'spotlights'
		ManagerGenerateRecapClicked: 'Manager recaps generate recap clicked',
		ManagerGenerateSpotlightClicked: 'Manager spotlights generate spotlight clicked',
	},

	/**
	 * Email Attribution Events
	 * Tracks when users arrive in the app from email links (e.g., manager digest, notifications)
	 * UTM params (utm_source, utm_medium, utm_campaign) are captured from the URL on page load.
	 */
	Email: {
		LinkVisited: 'Email Link Visited',
	},

	/**
	 * Profile Events
	 * Tracks profile page views and interactions
	 */
	Profile: {
		// Page view
		PageViewed: 'Profile Page Viewed',

		// Header actions
		GiveRecognitionButtonClicked: 'Profile Give Recognition Button Clicked',
		SettingsButtonClicked: 'Profile Settings Button Clicked',
		AskForAdviceClicked: 'Profile Ask For Advice Clicked',
		GiveAdviceClicked: 'Profile Give Advice Clicked',

		// Manager actions
		OneOnOneClicked: 'Profile One On One Clicked',
		CreateGoalClicked: 'Profile Create Goal Clicked',
		CreateCustomRecapClicked: 'Profile Create Custom Recap Clicked',
		CreateSpotlightClicked: 'Profile Create Spotlight Clicked',

		// Feed interactions
		TabChange: 'Profile Tab Change',
		RecognitionClicked: 'Profile Recognition Clicked',

		// Widget interactions
		TopConnectionClicked: 'Profile Top Connection Clicked',
		TopHashtagHover: 'Profile Top Hashtag Hover',
		TimeRangeChange: 'Profile Time Range Change',

		// Spotlight
		SpotlightBannerClicked: 'Profile Spotlight Banner Clicked',
	},

	/**
	 * Granola Events
	 * Tracks Granola connect/disconnect interactions
	 */
	Granola: {
		ConnectClicked: 'Granola Connect Clicked',
		Connected: 'Granola Connected',
		Disconnected: 'Granola Disconnected',
	},

	/**
	 * Linear Events
	 * Tracks Linear connect/disconnect interactions
	 */
	Linear: {
		ConnectClicked: 'Linear Connect Clicked',
		Connected: 'Linear Connected',
		Disconnected: 'Linear Disconnected',
	},

	/**
	 * Github Events
	 * Tracks GitHub connect/disconnect interactions
	 */
	Github: {
		ConnectClicked: 'Github Connect Clicked',
		Connected: 'Github Connected',
		Disconnected: 'Github Disconnected',
		OrgMembersViewed: 'Github Org Members Viewed',
		MemberMappingCreated: 'Github Member Mapping Created',
		MemberMappingRemoved: 'Github Member Mapping Removed',
		CompanyConnected: 'Github Company Connector Connected',
		OrgDreamingToggled: 'Github Org Dreaming Toggled',
		CompanyScopeSaved: 'Github Company Scope Saved',
		DreamingToggled: 'Github Dreaming Toggled',
	},

	/**
	 * Slack Events
	 * Tracks Slack connect/disconnect interactions
	 */
	Slack: {
		ConnectClicked: 'Slack Connect Clicked',
		Connected: 'Slack Connected',
		Disconnected: 'Slack Disconnected',
	},

	/**
	 * MS Teams Events
	 * Tracks Microsoft Teams connect/disconnect interactions
	 */
	MsTeams: {
		ConnectClicked: 'MS Teams Connect Clicked',
		Connected: 'MS Teams Connected',
		Disconnected: 'MS Teams Disconnected',
	},

	/**
	 * Video Call Telemetry Events
	 * Tracks RTC pipeline health, performance, and diagnostics during 1:1 video calls
	 */
	VideoCall: {
		AudioChunksDropped: 'Video Call Audio Chunks Dropped',
		AudioBufferSaturation: 'Video Call Audio Buffer Saturation',
		ConnectionQualitySample: 'Video Call Connection Quality Sample',
		ConnectionDegraded: 'Video Call Connection Degraded',
		HeapAnomalyDetected: 'Video Call Heap Anomaly Detected',
		HeapSnapshot: 'Video Call Heap Snapshot',
		TranscriptBackoffEscalated: 'Video Call Transcript Backoff Escalated',
		TranscriptDeliveryLatency: 'Video Call Transcript Delivery Latency',
		VideoOffDetectionCost: 'Video Call Video Off Detection Cost',
	},

	/**
	 * Seasonal Theme Events
	 * Tracks user interactions with the seasonal theme toggle and intro modal
	 */
	SeasonalTheme: {
		ToggleClicked: 'Seasonal Theme Toggle Clicked',
		Enabled: 'Seasonal Theme Enabled',
		Disabled: 'Seasonal Theme Disabled',
		ModalDismissed: 'Seasonal Theme Modal Dismissed',
		ModalAutoShown: 'Seasonal Theme Modal Auto Shown',
		ModalLinkOpened: 'Seasonal Theme Modal Link Opened',
		FallingEmojisToggled: 'Seasonal Theme Falling Emojis Toggled',
		BloomLevelChanged: 'Seasonal Theme Bloom Level Changed',
	},

	/**
	 * Workday Anytime Feedback Events
	 * Tracks admin interactions with the Workday Anytime Feedback integration settings
	 */
	WorkdayAnytimeFeedback: {
		PageView: 'Workday Anytime Feedback - Page View',
		TestConnection: 'Workday Anytime Feedback - Test Connection',
		SaveConfig: 'Workday Anytime Feedback - Save Config',
		Disconnect: 'Workday Anytime Feedback - Disconnect',
		SyncNow: 'Workday Anytime Feedback - Sync Now',
		FetchBadges: 'Workday Anytime Feedback - Fetch Badges',
		TelemetryRetrySyncRecord: 'Workday Anytime Feedback - Telemetry Retry Sync Record',
		TelemetryFilterChanged: 'Workday Anytime Feedback - Telemetry Filter Changed',
	},
};
